import {NetworkStatus} from 'apollo-client';
import {useMemo} from 'react';

import {
  SemanticLegendByMetric,
  useSemanticLegendsQueryWithCategoricalConfigQuery,
} from '../../../../../generated/graphql';
import {YAxisValues} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {constructSemanticLegendDataWithCategoricalConfig} from './constructSemanticLegendData';
import {useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig} from './useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig';

export function useSemanticLegendSettingsWithCategoricalConfig(
  viewId?: string,
  configKeys?: string[],
  localKey?: string,
  localYAxisValue?: YAxisValues
) {
  const {
    data: semanticLegendSettings,
    error: semanticLegendSettingsError,
    networkStatus,
  } = useSemanticLegendsQueryWithCategoricalConfigQuery({
    variables: {id: viewId ?? ''},
    skip: viewId === '',
    notifyOnNetworkStatusChange: true,
  });
  const semanticLegendSettingsLoading = networkStatus !== NetworkStatus.ready;

  // Get the aggregates for the current key whether it's local or actually the one saved on the server
  const currentKey =
    localKey ?? semanticLegendSettings?.view?.semanticLegend?.currentMetric; // TODO: change to currentKey

  const semanticLegendsByMetric = useMemo(
    () =>
      semanticLegendSettings?.view?.semanticLegend
        ?.semanticLegendsByMetric as SemanticLegendByMetric[],
    [semanticLegendSettings]
  );
  const getSemanticLegendPalette =
    useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig();
  const decodedSemanticLegendData = useMemo(
    () =>
      constructSemanticLegendDataWithCategoricalConfig(
        getSemanticLegendPalette,
        currentKey,
        semanticLegendsByMetric
      ),
    [currentKey, semanticLegendsByMetric, getSemanticLegendPalette]
  );

  const yAxisValue = localYAxisValue ?? decodedSemanticLegendData.yAxisValue;

  return useMemo(() => {
    // Calculate all skip conditions inside useMemo to prevent race conditions
    // Include loading state check to avoid inconsistent state transitions
    const shouldSkipMetricStatistics =
      semanticLegendSettingsLoading ||
      !currentKey ||
      currentKey === '' ||
      currentKey === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key ||
      (configKeys ?? []).includes(currentKey);

    const shouldSkipConfigValuesAggregation =
      semanticLegendSettingsLoading ||
      !currentKey ||
      currentKey === '' ||
      currentKey === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key ||
      yAxisValue === YAxisValues.Config_Categories ||
      !(configKeys ?? []).includes(currentKey);

    const shouldSkipConfigValuesCategories =
      semanticLegendSettingsLoading ||
      !currentKey ||
      currentKey === '' ||
      currentKey === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key ||
      yAxisValue !== YAxisValues.Config_Categories ||
      !(configKeys ?? []).includes(currentKey);

    return {
      constructedSemanticLegendData: decodedSemanticLegendData,
      currentKey,
      semanticLegendSettingsError,
      semanticLegendSettingsLoading,
      shouldSkipMetricStatistics,
      shouldSkipConfigValuesAggregation,
      shouldSkipConfigValuesCategories,
    };
  }, [
    currentKey,
    decodedSemanticLegendData,
    semanticLegendSettingsError,
    semanticLegendSettingsLoading,
    configKeys,
    yAxisValue,
  ]);
}
